import StickyFooter from "@components/quiz/StickyFooter"
import DailyHabitVisual from "@components/quiz/visuals/DailyHabitVisual"
import ForgettingCurveVisual from "@components/quiz/visuals/ForgettingCurveVisual"
import MindMapVisual from "@components/quiz/visuals/MindMapVisual"
import PersonalizedVisual from "@components/quiz/visuals/PersonalizedVisual"
import SocialProofVisual from "@components/quiz/visuals/SocialProofVisual"
import WelcomeVisual from "@components/quiz/visuals/WelcomeVisual"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import getPersonalization, {
  Personalization,
  toInlineLabel,
} from "../personalization"
import { InfoVisual } from "../quizSteps"
import StepHeading from "./StepHeading"

interface Props {
  id: string
  onContinue: () => void
  visual: InfoVisual
}

function renderVisual(visual: InfoVisual, personalization: Personalization) {
  switch (visual) {
    case "socialProof":
      return <SocialProofVisual />
    case "forgettingCurve":
      return <ForgettingCurveVisual />
    case "mindMapDemo":
      return <MindMapVisual />
    case "dailyHabit":
      return <DailyHabitVisual minutes={personalization.minutes} />
    case "personalized":
      return (
        <PersonalizedVisual
          items={[
            personalization.goalLabel,
            ...personalization.topicLabels,
            personalization.momentLabel,
          ].filter(Boolean)}
        />
      )
    case "welcome":
      return <WelcomeVisual />
    default: {
      const exhaustiveCheck: never = visual
      throw new Error(`Unhandled visual: ${String(exhaustiveCheck)}`)
    }
  }
}

export default function InfoStep({ id, onContinue, visual }: Props) {
  const { t } = useTranslation()
  const personalization = useMemo(() => getPersonalization(t), [t])

  const values = {
    goal: toInlineLabel(personalization.goalLabel),
    learnerType: toInlineLabel(personalization.learnerTypeLabel),
    minutes: personalization.minutes,
  }

  return (
    <div className="flex grow flex-col gap-6 pt-4">
      <StepHeading
        headingKey={`pages.onboarding.steps.${id}.heading`}
        subheadingKey={`pages.onboarding.steps.${id}.subheading`}
        values={values}
      />

      <div className="flex grow animate-fade-up items-center justify-center [animation-delay:150ms]">
        {renderVisual(visual, personalization)}
      </div>

      <StickyFooter>
        <button
          className="vm-button-primary"
          onClick={onContinue}
          type="button"
        >
          {t("shared.continue")}
        </button>
      </StickyFooter>
    </div>
  )
}
